import React, { useContext, useState, useCallback } from 'react';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import isEmpty from 'is-empty';
import Head from 'next/head';
import Api from '@/services/Api';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import ContentDetail from '@/components/ContentDetail/ContentDetail';
import { Language } from '@types';
import { Swiper, SwiperSlide } from 'swiper/react';
import ContentCard from '@/components/ContentCard/ContentCard';
import VideoUnavailablePage from '@/pages/video-unavailable';

import 'swiper/swiper.min.css';
import 'swiper/components/navigation/navigation.min.css';
import { useRouter } from 'next/router';
import DialogWishlist from '@/components/Dialog/DialogWishlist';
import ShareModal from '@/components/Dialog/ShareModal';
import { AppContext } from '@/contexts/app.context';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';

type Props = {
  movie: any;
};

const MoviePage: ({ movie }: Props) => boolean | JSX.Element = ({
  movie,
}: Props) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const { isWishlistModalOpen, onCancelWishlistModal, onAcceptWishlistModal } =
    useContext(AppContext);
  const [swiperInstance, setSwiperInstance] = useState<any>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const movePrevSlide = useCallback(() => {
    if (swiperInstance) swiperInstance.slidePrev();
  }, [swiperInstance]);

  const moveNextSlide = useCallback(() => {
    if (swiperInstance) swiperInstance.slideNext();
  }, [swiperInstance]);

  const handleOpenShareModal = () => {
    setIsShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };

  if (isEmpty(movie)) {
    return <VideoUnavailablePage />;
  }

  if (isEmpty(movie.finalvideoencodes?.final_url_20)) {
    return <VideoUnavailablePage isEmptyVideoUrl />;
  }

  return (
    <>
      <Head>
        <title> {movie.film_title} | Butaca</title>
        <meta
          name="description"
          content={
            locale === Language.ES ? movie.description_es : movie.description_en
          }
        />
      </Head>
      <div className="container mx-auto px-4 xl:px-0 py-12">
        <ContentDetail content={movie} onShare={handleOpenShareModal} />
        {movie?.similar_titles_list?.length > 0 && (
          <div>
            <div className="flex flex-row justify-between items-end">
              <h1 className="text-3xl font-semibold mt-14">
                {t('similarTitles')}
              </h1>
              <div className="hidden sm:flex flex-row justify-between items-center">
                <ChevronLeftIcon
                  onClick={movePrevSlide}
                  className="w-8 h-6 cursor-pointer channel-swipper-button-prev"
                />
                <ChevronRightIcon
                  onClick={moveNextSlide}
                  className="w-8 h-6 cursor-pointer channel-swipper-button-next"
                />
              </div>
            </div>
            <Swiper
              slidesPerView="auto"
              spaceBetween={20}
              className="!pt-4 lg:!-ml-8 lg:!pl-7 lg:!w-[calc(100%+2rem)]"
              onSwiper={(swiper) => setSwiperInstance(swiper)}
            >
              {movie.similar_titles_list.map((similarTitles, index) =>
                similarTitles.contents.map((content, contentIndex) => (
                  <SwiperSlide
                    key={`${similarTitles._id}-${content._id}`}
                    className="!w-auto"
                  >
                    <ContentCard
                      content={content}
                      loadPriority={index < 6 && contentIndex < 6}
                    />
                  </SwiperSlide>
                )),
              )}
            </Swiper>
          </div>
        )}
      </div>
      <DialogWishlist
        show={isWishlistModalOpen}
        onCancel={onCancelWishlistModal}
        onAccept={onAcceptWishlistModal}
      />
      <ShareModal
        show={isShareModalOpen}
        onClose={handleCloseShareModal}
        contentSlug={movie.slug}
        contentTitle={movie.film_title}
        contentType="movie"
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  req,
  locale,
  query,
}) => {
  const { slug } = query;

  const movie = await Api.get(`v2/contents/${slug}`, req);
  const data = movie || {};

  console.log('responded data: ', JSON.stringify(data.film_title));

  return {
    props: {
      movie: movie || {},
      ...(await serverSideTranslations(locale, ['common', 'profile'])),
    },
  };
};

export default MoviePage;
